import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ModalProps,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export type ConfirmModalAction = 'execute' | 'cancel' | '';

type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  confirmAction: ConfirmModalAction;
  onSuccess: (reason?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  withReason?: boolean;
};

export const ConfirmModal: FC<ConfirmModalProps> = (props) => {
  const { open, onClose, confirmAction, onSuccess, onCancel, isLoading, withReason } = props;
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    setReason('');
    onClose();
  };
  const handleSend = () => {
    if (!reason) {
      setIsError(true);
      return;
    }
    onSuccess(reason);
    setReason('');
    setIsError(false);
  };
  const closeHandler = () => {
    setReason('');
    onClose();
  };

  // cancel handler
  const cancelHandler = () => {
    setReason('');
    onCancel();
  };

  // variables
  const isExecute = confirmAction === 'execute';

  // with reason variant logic
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>
        {confirmAction
          ? isExecute
            ? t('transaction.modal.execute.title')
            : t('transaction.modal.cancel.title')
          : '-'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={closeHandler}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <div>
            {confirmAction
              ? isExecute
                ? t('transaction.modal.execute.text')
                : t('transaction.modal.cancel.text')
              : '-'}
          </div>

          {withReason ? (
            <TextField
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              label={t('ui.cancel-modal.reason')}
              fullWidth
              multiline
              minRows={3}
              error={!reason && isError}
            />
          ) : (
            <TextField
              name="ext_transaction_id"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              label={t('ui.cancel-modal.ext-transaction-id')}
              fullWidth
              error={!reason && isError}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={() => handleSend()} disabled={isLoading}>
          {t('transaction.modal.yes')}
        </Button>
        <Button variant="outlined" onClick={() => cancelHandler()} disabled={isLoading}>
          {t('transaction.modal.no')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
